// src/components/charts/ProblemTypesChart.js
import React from 'react';
import './ProblemTypesChart.css';
import { 
  calculatePublicationProblems, 
  calculateExpectedPublicationDays, 
  hasTimelinessProblems, 
  getTimelinessStatus,
  getSchoolTimeliness 
} from '../publicationCalculator';

const ProblemTypesChart = ({ districtData, filters }) => {
  const { problemStats, problemDescriptions, totalSchools, schoolsWithProblems, perfectSchools, timelinessStats } = React.useMemo(() => {
    if (!districtData.dates || districtData.dates.length === 0) {
      return { 
        problemStats: {}, 
        problemDescriptions: [], 
        totalSchools: 0, 
        schoolsWithProblems: 0, 
        perfectSchools: 0,
        timelinessStats: null
      };
    }

    const schools = districtData.dates;
    const totalSchools = schools.length;

    const stats = {
      menuActual: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Актуальность типового меню',
        description: 'Проверка актуальности типового меню'
      },
      calendarActual: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Актуальность календарного питания',
        description: 'Проверка актуальности календарного питания'
      },
      parentControl: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Актуальность родительского контроля',
        description: 'Проверка актуальности файлов родительского контроля'
      },
      menuCompliance: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Соответствие меню',
        description: 'Сравнение фактического и типового меню'
      },
      menuErrors: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Ошибки в меню',
        description: 'Проверка на наличие ошибок в ежедневном меню'
      },
      menuPublication: { 
        total: totalSchools, 
        problems: 0, 
        label: 'Публикация меню',
        description: 'Контроль регулярности публикации меню'
      }
    };

    // Отдельная статистика для своевременности
    let timelinessStats = {
      total: totalSchools,
      problems: 0,
      perfect: 0,
      byPercentage: {}
    };

    const problemSchoolsSet = new Set();

    schools.forEach(school => {
      const indicators = getSchoolTableIndicators(school, districtData, filters);

      if (indicators.tm.status === 'error') {
        stats.menuActual.problems++;
        problemSchoolsSet.add(school.Код);
      }

      if (indicators.kp.status === 'error') {
        stats.calendarActual.problems++;
        problemSchoolsSet.add(school.Код);
      }

      if (indicators.rk.status === 'error') {
        stats.parentControl.problems++;
        problemSchoolsSet.add(school.Код);
      }

      if (indicators.compliance.status === 'error') {
        stats.menuCompliance.problems++;
        problemSchoolsSet.add(school.Код);
      }

      if (indicators.errors.status === 'error') {
        stats.menuErrors.problems++;
        problemSchoolsSet.add(school.Код);
      }

      if (indicators.publication.status === 'error') {
        stats.menuPublication.problems++;
        problemSchoolsSet.add(school.Код);
      }

      // Статистика для своевременности
      const timelinessPercentage = getTimelinessPercentage(school, districtData.timeliness);
      if (timelinessPercentage !== null) {
        if (timelinessPercentage < 100) {
          timelinessStats.problems++;
          problemSchoolsSet.add(school.Код);
        } else {
          timelinessStats.perfect++;
        }
        
        // Группировка по процентам для детализации
        const range = Math.floor(timelinessPercentage / 10) * 10;
        const key = `${range}-${range + 9}%`;
        timelinessStats.byPercentage[key] = (timelinessStats.byPercentage[key] || 0) + 1;
      } else {
        // Нет данных - считаем проблемой
        timelinessStats.problems++;
        problemSchoolsSet.add(school.Код);
        timelinessStats.byPercentage['Нет данных'] = (timelinessStats.byPercentage['Нет данных'] || 0) + 1;
      }
    });

    const descriptions = [];
    Object.entries(stats).forEach(([key, stat]) => {
      if (stat.total > 0 && stat.problems > 0) {
        const percentage = Math.round(((stat.total - stat.problems) / stat.total) * 100);
        
        let status = '';
        let priority = '';
        let recommendation = '';
        
        if (percentage >= 90) {
          status = 'удовлетворительно';
          priority = 'низкий';
          recommendation = 'Рекомендуется провести выборочную проверку для подтверждения данных';
        } else if (percentage >= 70) {
          status = 'требует внимания';
          priority = 'средний';
          recommendation = 'Необходимо усилить контроль и оказать методическую помощь проблемным образовательным учреждениям';
        } else {
          status = 'критично';
          priority = 'высокий';
          recommendation = 'Требуется немедленное вмешательство и проведение внеплановых проверок';
        }
        
        descriptions.push({
          key,
          label: stat.label,
          description: stat.description,
          problemCount: stat.problems,
          totalCount: stat.total,
          percentage,
          status,
          priority,
          recommendation
        });
      }
    });

    const schoolsWithProblems = problemSchoolsSet.size;
    const perfectSchools = totalSchools - schoolsWithProblems;

    return {
      problemStats: stats,
      problemDescriptions: descriptions.sort((a, b) => a.percentage - b.percentage),
      totalSchools,
      schoolsWithProblems,
      perfectSchools,
      timelinessStats
    };
  }, [districtData, filters]);

  const DonutChart = ({ percentage, color, size = 120 }) => {
    const radius = (size / 2) - 15;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <svg className="pt-donut-chart" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle
          className="pt-donut-background"
          cx={size/2}
          cy={size/2}
          r={radius}
          strokeWidth="12"
        />
        <circle
          className="pt-donut-progress"
          cx={size/2}
          cy={size/2}
          r={radius}
          strokeWidth="12"
          stroke={color}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
      </svg>
    );
  };

  const createChart = (stat, key) => {
    if (stat.total === 0) return null;

    const percentage = Math.round(((stat.total - stat.problems) / stat.total) * 100);
    const color = percentage >= 90 ? '#4CAF50' : 
                  percentage >= 70 ? '#FF9800' : 
                  '#F44336';
    
    return (
      <div className="pt-chart-wrapper" key={key}>
        <div className="pt-chart-title">{stat.label}</div>
        <div className="pt-chart-container">
          <DonutChart percentage={percentage} color={color} />
          <div className="pt-chart-percentage">
            <div className="pt-percentage-value">{percentage}%</div>
            <div className="pt-chart-label">
              {stat.problems > 0 ? `👎 ${stat.problems}` : '👍'}
            </div>
          </div>
        </div>
        <div className="pt-chart-details">
          <div className="pt-chart-numbers">
            {stat.total - stat.problems} из {stat.total} образовательных учреждений
          </div>
        </div>
      </div>
    );
  };

  // Создание кругового графика для своевременности
  const createTimelinessChart = () => {
    if (!timelinessStats || timelinessStats.total === 0) return null;

    const perfectPercentage = Math.round((timelinessStats.perfect / timelinessStats.total) * 100);
    const color = timelinessStats.problems === 0 ? '#4CAF50' : '#F44336';
    
    return (
      <div className="pt-chart-wrapper timeliness-chart">
        <div className="pt-chart-title">⏱️ Своевременность (100% норма)</div>
        <div className="pt-chart-container">
          <DonutChart percentage={perfectPercentage} color={color} size={140} />
          <div className="pt-chart-percentage">
            <div className="pt-percentage-value">{perfectPercentage}%</div>
            <div className="pt-chart-label">
              {timelinessStats.problems > 0 ? `👎 ${timelinessStats.problems}` : '👍'}
            </div>
          </div>
        </div>
        <div className="pt-chart-details">
          <div className="pt-chart-numbers">
            {timelinessStats.perfect} из {timelinessStats.total} ОУ (100% своевременность)
          </div>
          {timelinessStats.problems > 0 && (
            <div className="pt-chart-warning">
              ⚠️ {timelinessStats.problems} ОУ имеют нарушение своевременности (менее 100%)
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!districtData.dates || districtData.dates.length === 0) {
    return (
      <div className="pt-no-data">
        Нет данных для отображения статистики проблем
      </div>
    );
  }

  return (
    <div className="problem-types-chart">
      <div className="pt-charts-grid">
        {createTimelinessChart()}
        {Object.entries(problemStats).map(([key, stat]) => 
          createChart(stat, key)
        )}
      </div>

      {problemDescriptions.length > 0 && (
        <div className="pt-problems-summary">
          <h4 className="pt-summary-title">Анализ проблем района</h4>
          
          <div className="pt-summary-stats">
            <div className="pt-stat-item">
              <span className="pt-stat-value">{schoolsWithProblems}</span>
              <span className="pt-stat-label">Образовательных учреждений с проблемами</span>
            </div>
            <div className="pt-stat-item">
              <span className="pt-stat-value">{perfectSchools}</span>
              <span className="pt-stat-label">Образовательных учреждений без проблем</span>
            </div>
            <div className="pt-stat-item">
              <span className="pt-stat-value">{problemDescriptions.length}</span>
              <span className="pt-stat-label">Типов проблем выявлено</span>
            </div>
          </div>

          <div className="pt-priority-issues">
            <h5 className="pt-priority-title">Наиболее критичные проблемы:</h5>
            <div className="pt-issues-list">
              {problemDescriptions.slice(0, 3).map((problem, index) => (
                <div key={problem.key} className="pt-issue-item">
                  <div className="pt-issue-header">
                    <span className="pt-issue-label">{problem.label}</span>
                    <span className={`pt-issue-priority pt-priority-${problem.priority}`}>
                      {problem.priority} приоритет
                    </span>
                  </div>
                  <div className="pt-issue-details">
                    <span className="pt-issue-count">
                      {problem.problemCount} образовательных учреждений ({problem.percentage}% соответствия)
                    </span>
                    <span className="pt-issue-recommendation">
                      {problem.recommendation}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-action-required">
            <h5 className="pt-action-title">Рекомендуемые действия:</h5>
            <ul className="pt-action-list">
              {problemDescriptions.some(problem => problem.priority === 'высокий') && (
                <li>Провести внеплановые проверки в образовательных учреждениях с критическими проблемами в течение недели</li>
              )}
              {problemDescriptions.some(problem => problem.priority === 'средний') && (
                <li>Организовать методическую помощь и консультации для проблемных образовательных учреждений</li>
              )}
              <li>Усилить контроль за выполнением рекомендаций и сроков исправления нарушений</li>
              <li>Провести совещание с ответственными лицами образовательных учреждений для разбора типичных ошибок</li>
              {problemDescriptions.some(problem => problem.label.includes('родительский контроль')) && (
                <li>Обеспечить актуализацию документов родительского контроля во всех образовательных учреждениях</li>
              )}
              {timelinessStats && timelinessStats.problems > 0 && (
                <li>Повысить оперативность размещения документов на сайте для достижения 100% своевременности</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {problemDescriptions.length === 0 && timelinessStats && timelinessStats.problems === 0 && (
        <div className="pt-no-problems">
          <h4 className="pt-no-problems-title">✅ Отличные показатели по району</h4>
          <p className="pt-no-problems-text">
            Во всех {totalSchools} образовательных учреждениях района не выявлено системных проблем с организацией питания. 
            Все показатели соответствуют установленным требованиям и нормативным документам. 
            Рекомендуется поддерживать текущий уровень контроля и продолжить регулярный мониторинг.
          </p>
        </div>
      )}
    </div>
  );
};

const getSchoolTableIndicators = (school, data, filters) => {
  const tmStatus = isDateOutdated(school.ДатаТМ) ? 'error' : 'success';
  const tmText = isDateOutdated(school.ДатаТМ) ? 'Неактуально' : 'Актуально';
  
  const kpStatus = isDateOutdatedcp(school.ДатаКП) ? 'error' : 'success';
  const kpText = isDateOutdatedcp(school.ДатаКП) ? 'Неактуально' : 'Актуально';
  
  const rkStatus = isDateOutdated(school.ДатаРК) ? 'error' : 'success';
  const rkText = isDateOutdated(school.ДатаРК) ? 'Неактуально' : 'Актуально';
  
  const schoolCompliance = data.compliance.filter(item => 
    item.ПищеблокНаименование.includes(school.Наименование)
  );
  const avgCompliance = schoolCompliance.length > 0 ? 
    schoolCompliance.reduce((sum, item) => sum + item.ПроцентСоблюдения, 0) / schoolCompliance.length : 100;
  const complianceStatus = avgCompliance >= 70 ? 'success' : 'error';
  const complianceText = avgCompliance >= 70 ? 'Соответствует' : 'Не соответствует';
  
  const hasErrors = data.errors.some(error => error.Пищеблок.includes(school.Наименование));
  const errorsStatus = hasErrors ? 'error' : 'success';
  const errorsText = hasErrors ? 'Есть ошибки' : 'Нет ошибок';
  
  const publicationProblems = calculatePublicationProblems(data, filters.month, filters.year);
  const hasPublicationProblems = publicationProblems.some(s => s.Код === school.Код);
  const publicationStatus = hasPublicationProblems ? 'error' : 'success';
  const publicationText = hasPublicationProblems ? 'Нерегулярно' : 'Регулярно';
  
  const timelinessStatus = hasTimelinessProblems(school, data.timeliness, 100) ? 'error' : 'success';
  const timelinessPercentage = getTimelinessPercentage(school, data.timeliness);
  const timelinessText = timelinessPercentage !== null 
    ? (timelinessPercentage >= 100 ? `Отлично (${timelinessPercentage}%)` : `Нарушение (${timelinessPercentage}%)`)
    : 'Нет данных';
  
  return {
    tm: { status: tmStatus, text: tmText },
    kp: { status: kpStatus, text: kpText },
    rk: { status: rkStatus, text: rkText },
    compliance: { status: complianceStatus, text: complianceText },
    errors: { status: errorsStatus, text: errorsText },
    publication: { status: publicationStatus, text: publicationText },
    timeliness: { status: timelinessStatus, text: timelinessText }
  };
};

const getTimelinessPercentage = (school, timelinessData) => {
  if (!timelinessData || timelinessData.length === 0) return null;
  const record = timelinessData.find(item => item.Пищеблок === school.Наименование);
  return record ? record.ПроцентСвоевременности : null;
};

const isDateOutdated = (dateString) => {
  if (!dateString) return true;
  const CUTOFF_DATE = '01.08.2025';
  const cutoff = new Date(CUTOFF_DATE.split('.').reverse().join('-'));
  const date = new Date(dateString.split('.').reverse().join('-'));
  return date < cutoff;
};

const isDateOutdatedcp = (dateString) => {
  if (!dateString) return true;
  const CUTOFF_DATE = '01.01.2026';
  const cutoff = new Date(CUTOFF_DATE.split('.').reverse().join('-'));
  const date = new Date(dateString.split('.').reverse().join('-'));
  return date < cutoff;
};

export default ProblemTypesChart;